import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import '../../styles/CommonStyles.css';
import apiConfig from '../config/apiConfig';

function CompletePaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const memberData = location.state || {};
  const isRenewal = memberData.isRenewal || false;

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-login');
      return;
    }

    // Fetch pending payment details
    fetchPendingPayment();
  }, []);

  const fetchPendingPayment = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
      const memberId = localStorage.getItem('memberId');

      // Fetch pending payment for this member
      const response = await fetch(`${apiConfig.BASE_URL}/payments/pending-payments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment details');
      }

      const data = await response.json();
      
      // Find appropriate payment based on whether it's renewal or registration
      const paymentType = isRenewal ? 'renewal' : 'registration';
      const pendingPayment = data.pending_payments?.find(p => p.payment_type === paymentType);
      
      if (pendingPayment) {
        setPaymentData(pendingPayment);
        setLoading(false);
      } else {
        // No pending payment found, create a new one
        console.log(`No pending ${paymentType} payment found, creating new payment order...`);
        await createNewPaymentOrder();
      }
    } catch (err) {
      console.error('Error fetching payment:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const createNewPaymentOrder = async () => {
    try {
      const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
      const memberId = localStorage.getItem('memberId');

      // Get member profile to determine membership type and fee
      const profileResponse = await fetch(`${apiConfig.MEMBER.PROFILE}?memberId=${memberId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch member profile');
      }

      const profileData = await profileResponse.json();

      // Fetch membership plans to get the correct fee
      const plansResponse = await fetch(`${apiConfig.BASE_URL}/public/membership-plans`);
      if (!plansResponse.ok) {
        throw new Error('Failed to fetch membership plans');
      }

      const plansData = await plansResponse.json();
      
      // Find the plan matching the member's membership type
      const membershipTypeMap = {
        'primary_member': 'PRIMARY MEMBER',
        'primary': 'PRIMARY MEMBER',
        'associate_member': 'ASSOCIATE MEMBER',
        'student_membership': 'STUDENT MEMBERSHIP',
        'financial_collegium': 'FINANCIAL COLLEGIUM',
        'academic_collegium': 'ACADEMIC COLLEGIUM',
        'institutional_collegium': 'INSTITUTIONAL COLLEGIUM'
      };

      const planName = membershipTypeMap[profileData.membershipType];
      const plan = plansData.plans?.find(p => p.name === planName);

      console.log("membershipType from API:", profileData.membershipType);
      console.log("Mapped planName:", planName);
      console.log("Available plans:", plansData.plans.map(p => p.name));
      
      if (!plan) {
        throw new Error('Membership plan not found');
      }

      // Determine endpoint and payment type based on whether it's renewal or registration
      const endpoint = isRenewal 
        ? `${apiConfig.MEMBER.RENEW_MEMBERSHIP}`
        : `${apiConfig.BASE_URL}/payments/create-order`;
      
      const paymentType = isRenewal ? 'renewal' : 'registration';

      // Create new payment order
      const orderResponse = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: isRenewal ? undefined : JSON.stringify({
          amount: plan.price,
          payment_type: paymentType
        })
      });

      if (!orderResponse.ok) {
        throw new Error('Failed to create payment order');
      }

      const orderData = await orderResponse.json();
      
      // Set payment data with the new order
      setPaymentData({
        id: orderData.payment_id,
        amount: isRenewal ? orderData.amount / 100 : plan.price, // Renewal returns amount in paise
        payment_type: paymentType,
        reference_id: orderData.razorpay_order_id,
        razorpay_key_id: orderData.razorpay_key_id,
        expires_at: orderData.expires_at
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error creating payment order:', err);
      setError(`Failed to create payment order: ${err.message}`);
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!paymentData) return;

    setProcessingPayment(true);

    try {
      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        
        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      // Initialize Razorpay payment
      const options = {
        key: paymentData.razorpay_key_id,
        amount: paymentData.amount * 100, // Convert to paise
        currency: 'INR',
        name: 'Bangalore Valuers Association',
        description: 'Membership Registration Fee',
        order_id: paymentData.reference_id,
        handler: async function (response) {
          try {
            // Show verifying payment overlay
            setVerifyingPayment(true);
            setProcessingPayment(false);
            
            // Verify payment with server
            const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
            const memberId = localStorage.getItem('memberId');
            
            const verifyResponse = await fetch(apiConfig.PUBLIC.PAYMENT_VERIFICATION, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                member_id: memberId
              })
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            const verifyData = await verifyResponse.json();
            
            // Clear payment required flag
            localStorage.removeItem('paymentRequired');
            
            // Hide verifying overlay
            setVerifyingPayment(false);
            
            // Show success modal
            const message = isRenewal 
              ? 'Payment successful! Your membership has been renewed.' 
              : 'Payment successful! Your membership is now active.';
            setSuccessMessage(message);
            setShowSuccessModal(true);
            
            // Redirect after 2 seconds
            setTimeout(() => {
              navigate('/programs');
            }, 2000);
          } catch (error) {
            console.error('Payment verification error:', error);
            setVerifyingPayment(false);
            alert('Payment verification failed. Please contact support.');
            setProcessingPayment(false);
          }
        },
        prefill: {
          name: memberData.memberName || '',
          email: localStorage.getItem('memberEmail') || ''
        },
        theme: {
          color: '#8B4513'
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to initialize payment. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/member-login');
  };

  if (loading) {
    return (
      <div className="page-container">
        <Header />
        <div className="page-header">
          <h1>COMPLETE PAYMENT</h1>
        </div>
        <section className="content-section">
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Loading payment details...</p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-container">
      <Header />
      
      {/* Verifying Payment Overlay */}
      {verifyingPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid var(--primary-color)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1.5rem'
          }}></div>
          <p style={{ 
            color: 'white', 
            fontSize: '1.2rem',
            fontWeight: '500',
            margin: 0
          }}>
            Verifying your payment...
          </p>
          <p style={{ 
            color: '#ccc', 
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            Please wait, do not close this window
          </p>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2.5rem',
            maxWidth: '450px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#d4edda',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 1.5rem',
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2 style={{ 
              color: '#28a745', 
              marginBottom: '1rem',
              fontSize: '1.8rem',
              fontWeight: '600'
            }}>
              Payment Successful!
            </h2>
            <p style={{ 
              color: '#666', 
              fontSize: '1.1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6'
            }}>
              {successMessage}
            </p>
            <p style={{ 
              color: '#999', 
              fontSize: '0.9rem',
              fontStyle: 'italic'
            }}>
              Redirecting you to programs page...
            </p>
          </div>
        </div>
      )}
      
      <div className="page-header">
        <h1>{isRenewal ? 'RENEW YOUR MEMBERSHIP' : 'COMPLETE YOUR PAYMENT'}</h1>
        <p>{isRenewal 
          ? 'Your membership has expired. Please renew to continue accessing all features' 
          : 'Complete your membership registration payment to access all features'}</p>
      </div>
      
      <section className="content-section">
        <div style={{ 
          maxWidth: '600px', 
          margin: '0 auto',
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: 'var(--box-shadow)'
        }}>
          {error ? (
            <div>
              <div style={{ 
                padding: '1rem',
                marginBottom: '1.5rem',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
                border: '1px solid #f5c6cb'
              }}>
                {error}
              </div>
              <button 
                onClick={handleLogout}
                className="btn btn-light"
                style={{ width: '100%' }}
              >
                Back to Login
              </button>
            </div>
          ) : (
            <>
              <div style={{ 
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '1rem',
                marginBottom: '2rem'
              }}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>
                  ⚠️ {isRenewal ? 'Membership Expired' : 'Payment Required'}
                </h3>
                <p style={{ margin: 0, color: '#856404' }}>
                  {isRenewal 
                    ? `Your membership expired on ${memberData.expiryDate ? new Date(memberData.expiryDate).toLocaleDateString() : 'N/A'}. Please renew to continue accessing all features.`
                    : 'Your registration is incomplete. Please complete the payment to activate your membership and access all features.'}
                </p>
              </div>

              {paymentData && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>
                    Payment Details
                  </h3>
                  
                  <div style={{ 
                    backgroundColor: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '4px',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: '600' }}>Membership Type:</span>
                      <span>{memberData.membershipType || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: '600' }}>Amount:</span>
                      <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                        ₹{paymentData.amount}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '600' }}>Payment Status:</span>
                      <span style={{ color: '#ffc107', fontWeight: '600' }}>Pending</span>
                    </div>
                  </div>

                  {paymentData.expires_at && (
                    <p style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '1.5rem' }}>
                      This payment link expires on {new Date(paymentData.expires_at).toLocaleString()}
                    </p>
                  )}

                  <button 
                    onClick={handlePayNow}
                    disabled={processingPayment}
                    className="btn btn-primary"
                    style={{ 
                      width: '100%',
                      padding: '1rem',
                      fontSize: '1.1rem',
                      marginBottom: '1rem',
                      opacity: processingPayment ? 0.7 : 1
                    }}
                  >
                    {processingPayment ? 'Processing...' : 'PAY NOW'}
                  </button>
                </div>
              )}

              <div style={{ 
                borderTop: '1px solid #eee',
                paddingTop: '1.5rem',
                textAlign: 'center'
              }}>
                <p style={{ marginBottom: '1rem', color: '#6c757d' }}>
                  Need help? Contact us at support@bangalorevaluers.org
                </p>
                <button 
                  onClick={handleLogout}
                  className="btn btn-light"
                  style={{ 
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #ddd'
                  }}
                >
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </section>
      
      <Footer />
      
      {/* Add CSS animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scaleIn {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default CompletePaymentPage;
